import React, { useState } from 'react';
import EditableBlock, { EditableBlockFooter } from './EditableBlock';
import sortSelectOptions from '../../utils/sortSelectOptions';
import { PROFESSION_OPTIONS } from '../../constants/contactEnums';

const formatMoney = (value) => {
  const num = parseFloat(value);
  if (isNaN(num) || num === 0) return null;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(num);
};

const KpiCard = ({ icon, label, value, accent = 'slate' }) => {
  const accentMap = {
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-100', txt: 'text-emerald-700' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-100', txt: 'text-blue-700' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-100', txt: 'text-purple-700' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-100', txt: 'text-amber-700' },
    slate: { bg: 'bg-slate-50', border: 'border-slate-200', txt: 'text-slate-600' },
  };
  const a = accentMap[accent] || accentMap.slate;
  return (
    <div className={`${a.bg} rounded-lg p-3 border ${a.border}`}>
      <p className={`text-[10px] font-bold uppercase tracking-wider ${a.txt} mb-1`}>
        <i className={`bi ${icon} mr-1`}></i>
        {label}
      </p>
      <p className="text-base font-bold text-slate-900 num">{value || <span className="text-slate-400">—</span>}</p>
    </div>
  );
};

const InputMoney = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
      {label}
    </label>
    <div className="relative">
      <input
        type="number"
        value={value === null || value === undefined ? '' : value}
        onChange={(e) => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
        data-testid={testid}
        min="0"
        step="1000"
        className="w-full pl-3 pr-7 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 outline-none"
        placeholder="0"
      />
      <span className="absolute right-3 top-1.5 text-slate-400 text-sm">€</span>
    </div>
  </div>
);

const FinancialProfileBlock = ({ contact, onSave }) => {
  const ic = contact?.informations_confidentielles || {};

  const buildInitial = (c) => ({
    apport_disponible: c?.informations_confidentielles?.apport_disponible ?? null,
    capacite_emprunt: c?.informations_confidentielles?.capacite_emprunt ?? null,
    revenus_annuels: c?.informations_confidentielles?.revenus_annuels ?? null,
    banque: c?.informations_confidentielles?.banque || '',
    nom_banquier: c?.informations_confidentielles?.nom_banquier || '',
    situation_pro: c?.informations_confidentielles?.situation_pro || '',
  });

  const [form, setForm] = useState(() => buildInitial(contact));

  React.useEffect(() => {
    setForm(buildInitial(contact));
  }, [contact?.id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Budget max estimé = apport + capacité
  const budgetMax = (parseFloat(form.apport_disponible) || 0) + (parseFloat(form.capacite_emprunt) || 0);
  const budgetMaxView = (parseFloat(ic.apport_disponible) || 0) + (parseFloat(ic.capacite_emprunt) || 0);

  const isQualified = ic.apport_disponible && ic.capacite_emprunt;

  const handleSave = async (save) => {
    await save({
      informations_confidentielles: {
        ...ic,
        apport_disponible: form.apport_disponible,
        capacite_emprunt: form.capacite_emprunt,
        revenus_annuels: form.revenus_annuels,
        banque: form.banque || null,
        nom_banquier: form.nom_banquier || null,
        situation_pro: form.situation_pro || null,
      },
    });
  };

  // Si pas d'infos financières en mode vue et bloc non développable → on n'affiche pas pour les non-acquéreurs
  // Mais on garde toujours le bouton d'édition pour pouvoir saisir des données.

  return (
    <EditableBlock
      title="Profil financier"
      icon="bi-piggy-bank"
      testid="contact-financial-block"
      onSave={onSave}
      extraHeader={
        isQualified ? (
          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-bold uppercase ml-1">
            Qualifié
          </span>
        ) : null
      }
      renderView={() => (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCard
            icon="bi-wallet2"
            label="Apport"
            value={formatMoney(ic.apport_disponible)}
            accent="emerald"
          />
          <KpiCard
            icon="bi-bank"
            label="Capacité emprunt"
            value={formatMoney(ic.capacite_emprunt)}
            accent="blue"
          />
          <KpiCard
            icon="bi-cash-stack"
            label="Budget max"
            value={budgetMaxView > 0 ? formatMoney(budgetMaxView) : null}
            accent="blue"
          />
          <KpiCard
            icon="bi-graph-up"
            label="Revenus annuels"
            value={formatMoney(ic.revenus_annuels)}
            accent="slate"
          />
          <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
              <i className="bi bi-briefcase mr-1"></i>
              Banque
            </p>
            {ic.banque ? (
              <>
                <p className="text-sm font-bold text-slate-900">{ic.banque}</p>
                {ic.nom_banquier && <p className="text-[10px] text-slate-500">{ic.nom_banquier}</p>}
              </>
            ) : (
              <p className="text-sm text-slate-400">—</p>
            )}
          </div>
        </div>
      )}
      renderEdit={({ closeEdit, save, saving }) => (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <InputMoney
              label="Apport disponible"
              value={form.apport_disponible}
              onChange={(v) => update('apport_disponible', v)}
              testid="fin-apport"
            />
            <InputMoney
              label="Capacité d'emprunt"
              value={form.capacite_emprunt}
              onChange={(v) => update('capacite_emprunt', v)}
              testid="fin-capacite"
            />
            <InputMoney
              label="Revenus annuels"
              value={form.revenus_annuels}
              onChange={(v) => update('revenus_annuels', v)}
              testid="fin-revenus"
            />
          </div>
          {budgetMax > 0 && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700">
              <i className="bi bi-info-circle mr-1"></i>
              <strong>Budget max estimé</strong> : <span className="num font-bold">{formatMoney(budgetMax)}</span>{' '}
              (apport + capacité d'emprunt)
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                Banque
              </label>
              <input
                type="text"
                value={form.banque}
                onChange={(e) => update('banque', e.target.value)}
                data-testid="fin-banque"
                className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/40 outline-none"
                placeholder="Nom de la banque"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                Banquier (contact)
              </label>
              <input
                type="text"
                value={form.nom_banquier}
                onChange={(e) => update('nom_banquier', e.target.value)}
                data-testid="fin-banquier"
                className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/40 outline-none"
                placeholder="Nom du conseiller"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                Situation professionnelle
              </label>
              <select
                value={form.situation_pro}
                onChange={(e) => update('situation_pro', e.target.value)}
                data-testid="fin-situation"
                className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/40 outline-none"
              >
                <option value="">—</option>
                {sortSelectOptions(PROFESSION_OPTIONS).map(o => (
                  <option key={o.value} value={o.label}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <EditableBlockFooter
            onCancel={closeEdit}
            onSave={() => handleSave(save)}
            saving={saving}
            testid="fin-block"
          />
        </div>
      )}
    />
  );
};

export default FinancialProfileBlock;
